import {
  ChartFrequency,
  IComposableChartsWidget,
  IPostgreJobsAnalyticsPayload,
} from 'wisestep-api';

import {
  SmsBarChartPostgre,
  SmsMetricChartPostgre,
  SmsRecruiterTablePostgre,
  SmsStackedBarChartPostgre,
} from '../../../../composableAnalytics/BaseCharts';
import { ChartScope } from '../../../types';
import { SmsWidget, getChartFreqForTimePeriod } from '../../helper';
import { jobsTimePeriodList } from '../../Jobs';

const smsWidgetsByTimeAndScope = (
  timePeriodId: string,
  scope: ChartScope,
  frequency: ChartFrequency = getChartFreqForTimePeriod[timePeriodId]
): Record<SmsWidget, IComposableChartsWidget> => {
  const defaultQuery: IPostgreJobsAnalyticsPayload = {
    category: 'sms',
    filters: [],
    type: 'sent-metric',
    range: timePeriodId,
    scope,
    frequency,
  };

  const widgetConfig: Record<SmsWidget, IComposableChartsWidget> = {
    [SmsWidget.METRIC_SENT]: {
      name: 'Total SMS Sent',
      filters: {},
      category: 'sms',
      type: `metric_sms_sent_${timePeriodId}_${scope}_postgre`,
      component: (
        <SmsMetricChartPostgre
          query={{ ...defaultQuery, type: 'sent-metric' }}
          title="SMS Sent"
          widgetName={SmsWidget.METRIC_SENT}
        />
      ),
    },
    [SmsWidget.METRIC_RECEIVED]: {
      name: 'Total SMS Received',
      filters: {},
      category: 'sms',
      type: `metric_sms_received_${timePeriodId}_${scope}_postgre`,
      component: (
        <SmsMetricChartPostgre
          query={{ ...defaultQuery, type: 'received-metric' }}
          title="SMS Received"
          widgetName={SmsWidget.METRIC_RECEIVED}
        />
      ),
    },
    [SmsWidget.TABLE_SENT]: {
      name: 'SMS Sent by Recruiters by Time',
      filters: {},
      category: 'sms',
      type: `table_sms_sent_recruiter_${timePeriodId}_${scope}_postgre`,
      component: (
        <SmsRecruiterTablePostgre
          query={{ ...defaultQuery, type: 'sent-recruiter-table' }}
          title="SMS Sent by Recruiters by Time"
          widgetName={SmsWidget.TABLE_SENT}
          widgetType={`table_sms_sent_recruiter_${timePeriodId}_${scope}_postgre`}
        />
      ),
    },
    [SmsWidget.TABLE_RECEIVED]: {
      name: 'SMS Received by Recruiters by Time',
      filters: {},
      category: 'sms',
      type: `table_sms_received_recruiter_${timePeriodId}_${scope}_postgre`,
      component: (
        <SmsRecruiterTablePostgre
          query={{ ...defaultQuery, type: 'received-recruiter-table' }}
          title="SMS Received by Recruiters by Time"
          widgetName={SmsWidget.TABLE_RECEIVED}
          widgetType={`table_sms_received_recruiter_${timePeriodId}_${scope}_postgre`}
        />
      ),
    },
    [SmsWidget.TABLE_SENT_USER_ONLY]: {
      name: 'SMS Sent by Recruiters',
      filters: {},
      category: 'sms',
      type: `table_sms_sent_recruiter_only_${timePeriodId}_${scope}_postgre`,
      component: (
        <SmsRecruiterTablePostgre
          query={{ ...defaultQuery, type: 'sent-recruiter-table' }}
          title="SMS Sent by Recruiters"
          widgetName={SmsWidget.TABLE_SENT_USER_ONLY}
          widgetType={`table_sms_sent_recruiter_only_${timePeriodId}_${scope}_postgre`}
        />
      ),
    },
    [SmsWidget.TABLE_RECEIVED_USER_ONLY]: {
      name: 'SMS Received by Recruiters',
      filters: {},
      category: 'sms',
      type: `table_sms_received_recruiter_only_${timePeriodId}_${scope}_postgre`,
      component: (
        <SmsRecruiterTablePostgre
          query={{ ...defaultQuery, type: 'received-recruiter-table' }}
          title="SMS Received by Recruiters"
          widgetName={SmsWidget.TABLE_RECEIVED_USER_ONLY}
          widgetType={`table_sms_received_recruiter_only_${timePeriodId}_${scope}_postgre`}
        />
      ),
    },
    [SmsWidget.TABLE_SENT_JOB]: {
      name: 'SMS Sent by Jobs',
      filters: {},
      category: 'sms',
      type: `table_sms_sent_recruiter_job_${timePeriodId}_${scope}_postgre`,
      component: (
        <SmsRecruiterTablePostgre
          query={{ ...defaultQuery, type: 'sent-job-table' }}
          title="SMS Sent by Jobs"
          widgetName={SmsWidget.TABLE_SENT_JOB}
          widgetType={`table_sms_sent_recruiter_job_${timePeriodId}_${scope}_postgre`}
        />
      ),
    },
    [SmsWidget.TABLE_RECEIVED_JOB]: {
      name: 'SMS Received by Jobs',
      filters: {},
      category: 'sms',
      type: `table_sms_received_recruiter_job_${timePeriodId}_${scope}_postgre`,
      component: (
        <SmsRecruiterTablePostgre
          query={{ ...defaultQuery, type: 'received-job-table' }}
          title="SMS Received by Jobs"
          widgetName={SmsWidget.TABLE_RECEIVED_JOB}
          widgetType={`table_sms_received_recruiter_job_${timePeriodId}_${scope}_postgre`}
        />
      ),
    },
    [SmsWidget.BAR_CHART_SENT]: {
      name: `SMS Sent Trends - ${frequency}`,
      filters: {},
      category: 'sms',
      type: `barchart_sms_sent_${timePeriodId}_${scope}_${frequency}_postgre`,
      component: (
        <SmsBarChartPostgre
          query={{ ...defaultQuery, type: 'sent-bar-chart' }}
          title={`SMS Sent Trends ${frequency}`}
          widgetName={SmsWidget.BAR_CHART_SENT}
        />
      ),
    },
    [SmsWidget.BAR_CHART_RECEIVED]: {
      name: `SMS Received Trends - ${frequency}`,
      filters: {},
      category: 'sms',
      type: `barchart_sms_received_${timePeriodId}_${scope}_${frequency}_postgre`,
      component: (
        <SmsBarChartPostgre
          query={{ ...defaultQuery, type: 'received-bar-chart' }}
          title={`SMS Received Trends ${frequency}`}
          widgetName={SmsWidget.BAR_CHART_RECEIVED}
        />
      ),
    },
    [SmsWidget.STACKEDBAR_CHART]: {
      name: `SMS Sent and Received - ${frequency}`,
      filters: {},
      category: 'sms',
      type: `stackedbarchart_sms_sent_received_${timePeriodId}_${scope}_${frequency}_postgre`,
      component: (
        <SmsStackedBarChartPostgre
          query={{ ...defaultQuery, type: 'stackedbar-chart' }}
          title={`SMS Sent & Received ${frequency}`}
          widgetName={SmsWidget.STACKEDBAR_CHART}
        />
      ),
    },
  };

  return widgetConfig;
};

export const smsWidgetsDashboardKeyPostgre = (
  timePeriodId: string,
  scope: ChartScope
): IComposableChartsWidget[] => {
  const defaultWidgetList = Object.values(smsWidgetsByTimeAndScope(timePeriodId, scope));
  const extraWidgetList = {};

  return [...defaultWidgetList, ...(extraWidgetList[timePeriodId] ?? [])];
};

export const postgreSmsDashboardKeysObjectList = jobsTimePeriodList.flatMap((timePeriod) => [
  ...smsWidgetsDashboardKeyPostgre(timePeriod?.id, ChartScope.WORKSPACE),
  ...smsWidgetsDashboardKeyPostgre(timePeriod?.id, ChartScope.LOGGED_IN_USER),
]);
